export const HOLD_REASON_CODES = [
  'WITHDRAWAL_AML_UNAVAILABLE',
  'WITHDRAWAL_AML_STALE',
  'WITHDRAWAL_AML_TIMEOUT',
  'WITHDRAWAL_AML_PROVIDER_MANUAL',
  'WITHDRAWAL_AML_REVIEW',
  'WITHDRAWAL_AML_REJECTED',
  'WITHDRAWAL_AML_RISK_CHANGED',
  'WHITELIST_ADDRESS_AML_BLOCKED',
  'COMPLIANCE_INFO_REQUESTED',
] as const;

export type HoldReasonCode = (typeof HOLD_REASON_CODES)[number];

const HOLD_REASON_SET = new Set<string>(HOLD_REASON_CODES);

export const holdReasonKey = (code: string | null | undefined): string =>
  code && HOLD_REASON_SET.has(code) ? `compliance.holdReason.${code}` : 'compliance.holdReason.unknown';

export const CHECK_PHASES = ['CREATE', 'APPROVE', 'EXECUTE', 'PRE_SIGN', 'PRE_BROADCAST', 'RECHECK'] as const;

export const phaseKey = (phase: string): string =>
  (CHECK_PHASES as readonly string[]).includes(phase) ? `compliance.phase.${phase}` : 'compliance.phase.unknown';

export const outcomeKey = (outcome: string): string => {
  if (outcome === 'PASS' || outcome === 'HOLD' || outcome === 'OVERRIDDEN') return `compliance.outcome.${outcome}`;
  return 'compliance.outcome.unknown';
};

export const decisionStateKey = (state: string): string =>
  ['APPLIED', 'PENDING_SECOND', 'REJECTED', 'CANCELLED'].includes(state)
    ? `compliance.decisionState.${state}`
    : 'compliance.decisionState.unknown';

export const addressBlockReasonKey = (reason: string | null | undefined): string => {
  if (reason === 'AML_HIGH_RISK' || reason === 'COMPLIANCE_RETURN') return `compliance.addressBlock.${reason}`;
  return 'compliance.addressBlock.unknown';
};

export const makerKindKey = (kind: string): string =>
  kind === 'HUMAN' || kind === 'AUTO' || kind === 'B2B' ? `compliance.maker.${kind}` : 'compliance.maker.unknown';

export const formatRiskScore = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toFixed(2);
};

export const riskBandClassName = (band: string | null | undefined): string => {
  if (band === 'LOW') return 'bg-success text-success-foreground';
  if (band === 'REVIEW') return 'bg-pending text-pending-foreground';
  if (band === 'HIGH') return 'bg-fail text-fail-foreground';
  return 'bg-muted text-muted-foreground';
};

export const formatRelativeHold = (iso: string | null | undefined, now = Date.now()): { unit: 'now' | 'minutes' | 'hours' | 'days'; count: number } => {
  if (!iso) return { unit: 'now', count: 0 };
  const delta = Math.max(0, now - new Date(iso).getTime());
  const minutes = Math.round(delta / 60000);
  if (minutes < 1) return { unit: 'now', count: 0 };
  if (minutes < 60) return { unit: 'minutes', count: minutes };
  const hours = Math.round(minutes / 60);
  if (hours < 48) return { unit: 'hours', count: hours };
  return { unit: 'days', count: Math.round(hours / 24) };
};

export const secondsToHours = (seconds: number): number => Math.round((seconds / 3600) * 100) / 100;

export const hoursToSeconds = (hours: number): number => Math.round(hours * 3600);

export const secondsToMinutes = (seconds: number): number => Math.round(seconds / 60);

export const minutesToSeconds = (minutes: number): number => Math.round(minutes * 60);

export const REASON_MIN = 3;
export const REASON_MAX = 1000;
export const MESSAGE_MAX = 2000;

export const isReasonValid = (value: string): boolean => {
  const length = value.trim().length;
  return length >= REASON_MIN && length <= REASON_MAX;
};
